import React, { useState, useEffect } from 'react';
import { getApplications, deleteApplication } from '../services/applicationService';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Container,
  Box,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  TableContainer,
  Grid
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function ApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect if the screen is small (mobile)
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedApplications,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(applications, 10);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const data = await getApplications();
      setApplications(data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await deleteApplication(id);
        fetchApplications(); // Re-fetch applications after delete
      } catch (error) {
        console.error('Error deleting application:', error);
      }
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <Box mt={4}>
          <AdminPageHeader
            eyebrow="Operations"
            title="Applications"
            description="Review submitted applications and open attached resumes quickly."
          />

          {isMobile ? (
            // For mobile screens, use Card layout
            <Grid container spacing={2}>
              {paginatedApplications.map((app) => (
                <Grid item xs={12} key={app.applicationId}>
                  <AdminSection sx={{ p: 0 }}>
                  <Card variant="outlined" sx={{ mb: 0, border: 0, boxShadow: 'none' }}>
                    <CardContent>
                      <Box sx={{ fontWeight: 600, mb: 1 }}>{app.name}</Box>
                      <Box>Email: {app.email}</Box>
                      <Box>Address: {app.address}</Box>
                      <Box>
                        Resume:{" "}
                        <Button
                          variant="outlined"
                          onClick={() => window.open(app.cvUrl, '_blank')}
                        >
                          View Resume
                        </Button>
                      </Box>
                      <Box>Date Applied: {new Date(app.submittedAt).toLocaleString()}</Box>
                    </CardContent>
                    <CardActions>
                      <IconButton color="error" onClick={() => handleDelete(app.applicationId)}>
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                  </AdminSection>
                </Grid>
              ))}
              {!applications.length && (
                <Grid item xs={12}>
                  <AdminSection>
                    <AdminEmptyState title="No applications yet" description="New submissions will appear here." />
                  </AdminSection>
                </Grid>
              )}
            </Grid>
          ) : (
            // For desktop/tablet screens, use Table layout
            <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Resume</TableCell>
                    <TableCell>Date Applied</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedApplications.map((app) => (
                    <TableRow key={app.applicationId}>
                      <TableCell>{app.name}</TableCell>
                      <TableCell>{app.email}</TableCell>
                      <TableCell>{app.address}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          onClick={() => window.open(app.cvUrl, '_blank')}
                        >
                          View Resume
                        </Button>
                      </TableCell>
                      <TableCell>{new Date(app.submittedAt).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleDelete(app.applicationId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!applications.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <AdminEmptyState title="No applications yet" description="New submissions will appear here." />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
            </AdminSection>
          )}
          {isMobile ? (
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ) : null}
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default ApplicationsPage;
